/**
 * What Create says afterwards — `docs/new-request.md` §11.4, Boards 9 C, 16 C.
 *
 * A dark card at the foot of the page: a green tick, what was made, what was
 * sent, and **Open the job** · **New request**. 640 wide and centred on a
 * desktop; on a phone the full width less 12 px, its two buttons side by side
 * under the text. The boards draw no ✕, so it leaves by itself after twelve
 * seconds — not while the pointer is on it, because a toast that goes as
 * somebody reaches for its button is worse than one that stays — or on Esc.
 */
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'

const STAYS_MS = 12_000

export interface RequestToastProps {
  title: string
  sub: string
  /** Absent for *Create, book later*, which has already opened the job. */
  onOpenJob?: () => void
  onNewRequest: () => void
  onDismiss: () => void
}

function Tick() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l3 3 5-6" />
    </svg>
  )
}

export function RequestToast({ title, sub, onOpenJob, onNewRequest, onDismiss }: RequestToastProps) {
  const phone = usePhone()
  const timer = useRef<number | null>(null)
  const start = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(onDismiss, STAYS_MS)
  }
  const hold = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
  }
  useEffect(() => {
    start()
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onDismiss() }
    window.addEventListener('keydown', onKey)
    return () => { hold(); window.removeEventListener('keydown', onKey) }
    /* Once per toast; a new toast is a new mount. */
  }, [])
  /*
   * The sheet lands on a record now, and a phone's record page owns the
   * bottom edge with its tab bar: the toast sits 18px above that instead of
   * over its labels. The page arrives after the toast does, so it looks again
   * while it is up.
   */
  const [lift, setLift] = useState(0)
  useEffect(() => {
    if (!phone) return
    const look = () => {
      const bar = document.querySelector<HTMLElement>('nav[aria-label="Record"]')
      setLift(bar ? Math.round(bar.getBoundingClientRect().height) : 0)
    }
    look()
    const every = window.setInterval(look, 250)
    return () => window.clearInterval(every)
  }, [phone])

  const button = cn('inline-flex items-center justify-center whitespace-nowrap border border-toast-line',
    'font-semibold text-toast-text hover:bg-toast-text/10',
    phone ? 'h-[38px] flex-1 rounded-[10px] text-[13.5px]' : 'h-8 rounded-button px-2.5 text-[12.5px]')

  if (phone) {
    return (
      <div role="status" onPointerEnter={hold} onPointerLeave={start} style={{ bottom: 18 + lift }}
        className="fixed inset-x-3 z-[70] rounded-[14px] bg-toast px-3.5 py-3 text-toast-text shadow-[0_14px_34px_rgba(20,20,10,.28)]">
        <div className="flex items-start gap-2.5">
          <span className="inline-flex text-toast-accent"><Tick /></span>
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="text-[14px] font-bold">{title}</b>
            <span className="text-[12px] text-toast-muted">{sub}</span>
          </div>
        </div>
        <div className="mt-2.5 flex gap-2">
          {onOpenJob && <button type="button" className={button} onClick={onOpenJob}>Open the job</button>}
          <button type="button" className={button} onClick={onNewRequest}>New request</button>
        </div>
      </div>
    )
  }

  return (
    <div role="status" onPointerEnter={hold} onPointerLeave={start}
      className="fixed bottom-6 left-1/2 z-[70] flex w-[640px] max-w-[calc(100vw-24px)] -translate-x-1/2 items-center gap-3 rounded-[12px] bg-toast py-3 pl-4 pr-3.5 text-toast-text shadow-[0_14px_34px_rgba(20,20,10,.28)]">
      <span className="inline-flex text-toast-accent"><Tick /></span>
      {/* 17 + 15 inside 12px either way: the 56px toast of Board 9 C. */}
      <div className="flex min-w-0 flex-col">
        <b className="truncate text-[13.5px] font-bold leading-[17px]">{title}</b>
        <span className="truncate text-[12px] leading-[15px] text-toast-muted">{sub}</span>
      </div>
      <span className="flex-1" />
      {onOpenJob && <button type="button" className={button} onClick={onOpenJob}>Open the job</button>}
      <button type="button" className={button} onClick={onNewRequest}>New request</button>
    </div>
  )
}
